import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaShoppingCart, 
  FaStore, 
  FaChartLine, 
  FaBox, 
  FaEye, 
  FaEdit, 
  FaMoneyBillWave,
  FaUsers,
  FaCalendarAlt,
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaCheckCircle,
  FaBoxOpen,
  FaTags,
  FaPlus,
  FaShoppingBag
} from 'react-icons/fa';
import Layout from '../../components/layout/Layout';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { getSellerProducts } from '../../services/productService';
import './Dashboard.css';

const SellerDashboard = () => {
  const { currentUser } = useAuth();
  
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    totalOrders: 0,
    totalSales: 0,
    pendingOrders: 0
  });
  
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError('');
      
      try {
        // Fetch seller's products
        const productsResponse = await getSellerProducts();
        const sellerProducts = productsResponse.results || [];
        
        // Calculate product stats
        const activeProducts = sellerProducts.filter(p => p.status === 'active');
        
        // Set recent products (latest 5)
        const sortedProducts = [...sellerProducts].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setRecentProducts(sortedProducts.slice(0, 5));
        
        // Fetch orders
        const ordersResponse = await api.get('/orders/');
        const allOrders = ordersResponse.data.results || [];
        
        // Filter for orders containing seller's products
        // This is a simplified approach - in a real app, you'd have a proper API endpoint
        const sellerOrders = allOrders.filter(order => 
          order.items && order.items.some(item => 
            sellerProducts.some(product => product.id === item.product)
          )
        );
        
        // Calculate order stats
        const pendingOrders = sellerOrders.filter(o => 
          o.status === 'pending' || o.status === 'processing'
        );
        
        const totalSales = sellerOrders.reduce((sum, order) => {
          // Sum only items belonging to this seller
          const sellerItems = order.items.filter(item => 
            sellerProducts.some(product => product.id === item.product)
          );
          
          return sum + sellerItems.reduce((itemSum, item) => 
            itemSum + (parseFloat(item.price) * item.quantity), 0
          );
        }, 0);
        
        // Set recent orders (latest 5)
        const sortedOrders = [...sellerOrders].sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setRecentOrders(sortedOrders.slice(0, 5));
        
        // Update stats
        setStats({
          totalProducts: sellerProducts.length,
          activeProducts: activeProducts.length,
          totalOrders: sellerOrders.length,
          totalSales: totalSales,
          pendingOrders: pendingOrders.length
        });
        
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [currentUser?.id]);
  
  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-3">Loading dashboard data...</p>
        </div>
      </Layout>
    );
  }
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };

  return (
    <Layout>
      <div className="seller-dashboard-container">
        <Container className="py-4">
          {/* Header Section */}
          <motion.div 
            className="dashboard-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h1 className="dashboard-title">Dashboard</h1>
                <p className="dashboard-subtitle">Welcome back, {currentUser?.first_name || 'Seller'}!</p>
              </div>
              <Button 
                as={Link} 
                to="/seller/add-product" 
                className="add-product-btn"
                size="lg"
              >
                <FaPlus className="me-2" />
                Add Product
              </Button>
            </div>
          </motion.div>
          
          {error && <div className="alert alert-danger">{error}</div>}
          
          {/* Stats Cards Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Row className="g-4 mb-4">
              <Col lg={3} md={6}>
                <motion.div variants={itemVariants}>
                  <Card className="stat-card stat-card-products">
                    <Card.Body>
                      <div className="stat-card-header">
                        <div className="stat-icon-wrapper">
                          <FaBoxOpen className="stat-icon" />
                        </div>
                        <div className="stat-trend positive">
                          <FaArrowUp className="me-1" />
                          <span>12%</span>
                        </div>
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-value">{stats.totalProducts}</h3>
                        <p className="stat-label">Total Products</p>
                        <div className="stat-footer">
                          <span className="stat-detail">
                            <FaCheckCircle className="me-1" />
                            {stats.activeProducts} active
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
          
              <Col lg={3} md={6}>
                <motion.div variants={itemVariants}>
                  <Card className="stat-card stat-card-orders">
                    <Card.Body>
                      <div className="stat-card-header">
                        <div className="stat-icon-wrapper">
                          <FaShoppingBag className="stat-icon" />
                        </div>
                        <div className="stat-trend positive">
                          <FaArrowUp className="me-1" />
                          <span>8%</span>
                        </div>
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-value">{stats.totalOrders}</h3>
                        <p className="stat-label">Total Orders</p>
                        <div className="stat-footer">
                          <span className="stat-detail">
                            <FaClock className="me-1" />
                            {stats.pendingOrders} pending
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
          
              <Col lg={3} md={6}>
                <motion.div variants={itemVariants}>
                  <Card className="stat-card stat-card-sales">
                    <Card.Body>
                      <div className="stat-card-header">
                        <div className="stat-icon-wrapper">
                          <FaMoneyBillWave className="stat-icon" />
                        </div>
                        <div className="stat-trend positive">
                          <FaArrowUp className="me-1" />
                          <span>24%</span>
                        </div>
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-value">GH₵ {stats.totalSales.toFixed(2)}</h3>
                        <p className="stat-label">Total Revenue</p>
                        <div className="stat-footer">
                          <span className="stat-detail">All time earnings</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
              
              <Col lg={3} md={6}>
                <motion.div variants={itemVariants}>
                  <Card className="stat-card stat-card-views">
                    <Card.Body>
                      <div className="stat-card-header">
                        <div className="stat-icon-wrapper">
                          <FaChartLine className="stat-icon" />
                        </div>
                        <div className="stat-trend positive">
                          <FaArrowUp className="me-1" />
                          <span>15%</span>
                        </div>
                      </div>
                      <div className="stat-content">
                        <h3 className="stat-value">2.4K</h3>
                        <p className="stat-label">Product Views</p>
                        <div className="stat-footer">
                          <span className="stat-detail">This month</span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            </Row>
          </motion.div>
        
          {/* Recent Products & Orders Grid */}
          <Row className="g-4">
            <Col lg={7}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="dashboard-card recent-products-card">
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="card-title mb-0">
                          <FaTags className="me-2 text-primary" />
                          Recent Products
                        </h5>
                      </div>
                      <Link to="/seller/products" className="view-all-link">
                        View All
                      </Link>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {recentProducts.length > 0 ? (
                      <div className="products-list">
                        {recentProducts.map((product, index) => (
                          <motion.div
                            key={product.id}
                            className="product-item"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <div className="product-info">
                              <div className="product-image">
                                {product.images && product.images[0] ? (
                                  <img src={product.images[0].image} alt={product.name} />
                                ) : (
                                  <div className="product-placeholder">
                                    <FaBox />
                                  </div>
                                )}
                              </div>
                              <div className="product-details">
                                <h6 className="product-name">{product.name}</h6>
                                <p className="product-price">GH₵ {parseFloat(product.price).toFixed(2)}</p>
                              </div>
                            </div>
                            <div className="product-actions">
                              <Badge bg={product.status === 'active' ? 'success' : 'secondary'} className="status-badge">
                                {product.status}
                              </Badge>
                              <div className="action-buttons">
                                <Link to={`/seller/products/${product.id}`} className="action-btn view-btn">
                                  <FaEye />
                                </Link>
                                <Link to={`/seller/edit-product/${product.id}`} className="action-btn edit-btn">
                                  <FaEdit />
                                </Link>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <FaBoxOpen className="empty-icon" />
                        <h5>No Products Yet</h5>
                        <p>Start by adding your first product</p>
                        <Button as={Link} to="/seller/add-product" className="add-first-btn">
                          <FaPlus className="me-2" />
                          Add Your First Product
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="dashboard-card recent-orders-card">
                  <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <h5 className="card-title mb-0">
                          <FaShoppingCart className="me-2 text-success" />
                          Recent Orders
                        </h5>
                      </div>
                      <Link to="/seller/orders" className="view-all-link">
                        View All
                      </Link>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {recentOrders.length > 0 ? (
                      <div className="orders-list">
                        {recentOrders.map((order, index) => (
                          <motion.div
                            key={order.id}
                            className="order-item"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <div className="order-header">
                              <span className="order-id">#{order.id}</span>
                              <Badge bg={
                                order.status === 'completed' ? 'success' :
                                order.status === 'processing' ? 'info' :
                                order.status === 'cancelled' ? 'danger' :
                                'warning'
                              }>
                                {order.status}
                              </Badge>
                            </div>
                            <div className="order-details">
                              <div className="order-buyer">
                                <FaUsers className="me-2" />
                                {order.buyer?.first_name || 'Unknown'} {order.buyer?.last_name || ''}
                              </div>
                              <div className="order-total">
                                GH₵ {parseFloat(order.total).toFixed(2)}
                              </div>
                            </div>
                            <div className="order-date">
                              <FaCalendarAlt className="me-1" />
                              {new Date(order.created_at).toLocaleDateString()}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state">
                        <FaShoppingCart className="empty-icon" />
                        <h5>No Orders Yet</h5>
                        <p>Your orders will appear here</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

export default SellerDashboard; 